import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { assignmentService, type AssignmentWithDetails, type ProgramItemWithProgress } from '../../services'
import { useAuthStore } from '../../stores'
import { ProgressBar, ProgressTable, ExtendAssignmentModal } from '../../components/assignment'
import { Button, LoadingScreen } from '../../components/common'
import { VideoPlayerModal } from '../../components/video'
import { useToast } from '../../components/common/Toast'
import type { AssignmentStatus } from '../../types/database.types'

const statusColors: Record<AssignmentStatus, { bg: string; text: string }> = {
  active: { bg: 'bg-green-100', text: 'text-green-800' },
  completed: { bg: 'bg-blue-100', text: 'text-blue-800' },
  expired: { bg: 'bg-red-100', text: 'text-red-800' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-800' },
}

export function AssignmentDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { profile } = useAuthStore()
  const toast = useToast()

  const [assignment, setAssignment] = useState<AssignmentWithDetails | null>(
    null
  )
  const [progressItems, setProgressItems] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedVideoForPlay, setSelectedVideoForPlay] = useState<ProgramItemWithProgress['video'] | null>(null)
  const [showExtendModal, setShowExtendModal] = useState(false)

  useEffect(() => {
    if (id) {
      loadAssignment()
    }
  }, [id])

  const loadAssignment = async () => {
    if (!id) return
    setIsLoading(true)
    try {
      const data = await assignmentService.getAssignmentById(id)
      setAssignment(data)

      // If we have a client, load full progress
      if (data?.client_id) {
        const progressData = await assignmentService.getAssignmentWithProgress(
          id,
          data.client_id
        )
        if (progressData) {
          setProgressItems(progressData.program.items)
        }
      }
    } catch (error) {
      console.error('Failed to load assignment:', error)
      toast.error('Failed to load assignment')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = async () => {
    if (!id) return
    if (!confirm('Cancel this assignment? The client will lose access.')) {
      return
    }
    try {
      await assignmentService.cancelAssignment(id)
      toast.success('Assignment cancelled')
      loadAssignment()
    } catch (error) {
      toast.error('Failed to cancel assignment')
    }
  }

  const handleComplete = async () => {
    if (!id) return
    try {
      await assignmentService.completeAssignment(id)
      toast.success('Assignment marked as complete')
      loadAssignment()
    } catch (error) {
      toast.error('Failed to complete assignment')
    }
  }

  const handleDelete = async () => {
    if (!id) return
    if (!confirm('Delete this assignment? This action cannot be undone.')) {
      return
    }
    try {
      await assignmentService.deleteAssignment(id)
      toast.success('Assignment deleted')
      navigate('/dashboard/assignments')
    } catch (error) {
      toast.error('Failed to delete assignment')
    }
  }

  const copyAccessLink = async () => {
    if (!assignment?.access_token) return
    const link = assignmentService.getAssignmentAccessLink(assignment.access_token)
    try {
      await navigator.clipboard.writeText(link)
      toast.success('Access link copied!')
    } catch (error) {
      toast.error('Failed to copy link')
    }
  }

  const handleExtendSuccess = (createdCount: number) => {
    setShowExtendModal(false)
    toast.success(`Program assigned to ${createdCount} additional client${createdCount !== 1 ? 's' : ''}`)
  }

  if (isLoading) {
    return <LoadingScreen message="Loading assignment..." />
  }

  if (!assignment) {
    return (
      <div className="text-center py-12">
        <h1 className="text-2xl font-bold text-gray-900">Assignment Not Found</h1>
        <Link to="/dashboard/assignments" className="mt-4 inline-block">
          <Button variant="outline">Back to Assignments</Button>
        </Link>
      </div>
    )
  }

  const statusColor = statusColors[assignment.status]
  const progress = assignment.progress_summary

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link
            to="/dashboard/assignments"
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                {assignment.program?.title || 'Assignment'}
              </h1>
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColor.bg} ${statusColor.text}`}
              >
                {assignment.status}
              </span>
            </div>
            <p className="text-gray-600">
              Assigned to {assignment.client?.full_name || 'Unknown'}
            </p>
          </div>
        </div>
        {assignment.status === 'active' && (
          <div className="flex gap-2">
            {profile && profile.subscription_tier !== 'free' && (
              <Button onClick={() => setShowExtendModal(true)}>
                Assign to More Clients
              </Button>
            )}
            <Button variant="outline" onClick={handleComplete}>
              Mark Complete
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* Progress summary */}
      {progress && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
          <h2 className="font-semibold text-gray-900 mb-4">Progress Overview</h2>
          <ProgressBar
            completed={progress.completed_items}
            total={progress.total_items}
            size="lg"
            color={
              progress.progress_percent === 100
                ? 'green'
                : progress.progress_percent > 50
                ? 'blue'
                : 'yellow'
            }
          />
        </div>
      )}

      {/* Assignment details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Client</h3>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
              <span className="text-lg font-medium text-gray-600">
                {assignment.client?.full_name?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
            <div>
              <p className="font-medium text-gray-900">
                {assignment.client?.full_name}
              </p>
              <p className="text-sm text-gray-500">
                {assignment.client?.email}
              </p>
            </div>
          </div>
          <Link
            to={`/dashboard/clients/${assignment.client_id}`}
            className="mt-4 inline-block text-sm text-blue-600 hover:text-blue-800"
          >
            View Client Profile
          </Link>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Details</h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm text-gray-500">Assigned</dt>
              <dd className="text-gray-900">
                {new Date(assignment.assigned_at).toLocaleDateString()}
              </dd>
            </div>
            {assignment.expires_at && (
              <div>
                <dt className="text-sm text-gray-500">Expires</dt>
                <dd className="text-gray-900">
                  {new Date(assignment.expires_at).toLocaleDateString()}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-sm text-gray-500">Program</dt>
              <dd className="text-gray-900">
                <Link
                  to={`/dashboard/programs/${assignment.program_id}`}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {assignment.program?.title}
                </Link>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Access link */}
      {assignment.status === 'active' && assignment.access_token && (
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-6 mb-8">
          <h3 className="font-semibold text-blue-900 mb-2">Client Access Link</h3>
          <p className="text-sm text-blue-700 mb-3">
            Share this link with your client so they can access their program.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={assignmentService.getAssignmentAccessLink(
                assignment.access_token
              )}
              className="flex-1 px-3 py-2 bg-white border border-blue-300 rounded-lg text-sm font-mono text-blue-800"
            />
            <Button onClick={copyAccessLink}>Copy Link</Button>
          </div>
        </div>
      )}

      {/* Notes */}
      {assignment.notes && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
          <h3 className="font-semibold text-gray-900 mb-2">Notes for Client</h3>
          <p className="text-gray-600 whitespace-pre-wrap">{assignment.notes}</p>
        </div>
      )}

      {/* Progress table */}
      {progressItems.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
          <h3 className="font-semibold text-gray-900 mb-4">Exercise Progress</h3>
          <ProgressTable
            items={progressItems}
            onPreview={(video) => setSelectedVideoForPlay(video)}
          />
        </div>
      )}

      {/* Danger zone */}
      <div className="border border-red-200 rounded-xl p-6">
        <h3 className="font-semibold text-red-600 mb-2">Danger Zone</h3>
        <p className="text-sm text-gray-600 mb-4">
          Deleting this assignment will remove all progress data. This action
          cannot be undone.
        </p>
        <Button variant="outline" onClick={handleDelete}>
          Delete Assignment
        </Button>
      </div>

      {/* Video preview modal */}
      <VideoPlayerModal
        isOpen={!!selectedVideoForPlay}
        onClose={() => setSelectedVideoForPlay(null)}
        video={selectedVideoForPlay}
      />

      {/* Extend assignment modal */}
      {profile && assignment && (
        <ExtendAssignmentModal
          isOpen={showExtendModal}
          onClose={() => setShowExtendModal(false)}
          onSuccess={handleExtendSuccess}
          programId={assignment.program_id}
          programTitle={assignment.program?.title || 'Program'}
          programDurationWeeks={assignment.program?.duration_weeks}
          providerId={profile.id}
          providerTier={profile.subscription_tier}
          originalNotes={assignment.notes}
        />
      )}
    </div>
  )
}
